import React, { useContext, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { AuthContext } from "../../AuthContext";
import { getStockJournal, getStockLevel } from "../../api";

const transactionTypeOptions = [
  { value: "ALL", label: "Alle Bewegungen" },
  { value: "RECEIPT", label: "Wareneingang" },
  { value: "ISSUE", label: "Warenausgang" },
  { value: "MOVE", label: "Umbuchung" },
  { value: "ADJUST", label: "Korrektur" },
  { value: "RESERVE", label: "Reservierung" },
  { value: "RELEASE", label: "Freigabe" },
];

function formatNumber(value) {
  const safeValue = typeof value === "number" && Number.isFinite(value) ? value : 0;
  return new Intl.NumberFormat("de-DE").format(safeValue);
}

function formatDateTime(value) {
  if (!value) {
    return "—";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "—";
  }
  return date.toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" });
}

function resolveStockLevel(data) {
  if (!data) {
    return null;
  }
  if (data.level) {
    return data.level;
  }
  if (data.stockLevel) {
    return data.stockLevel;
  }
  return data;
}

function resolveJournalEntries(data) {
  if (!data) {
    return [];
  }
  if (Array.isArray(data)) {
    return data;
  }
  if (Array.isArray(data.items)) {
    return data.items;
  }
  if (Array.isArray(data.entries)) {
    return data.entries;
  }
  return [];
}

function buildAvailability(level) {
  const onHand = typeof level?.onHand === "number" ? level.onHand : 0;
  const reserved = typeof level?.reserved === "number" ? level.reserved : 0;
  if (typeof level?.available === "number") {
    return level.available;
  }
  return onHand - reserved;
}

export default function InventoryDetailPage() {
  const { token, roles, hasRole } = useContext(AuthContext);
  const navigate = useNavigate();
  const { id: stockLevelId } = useParams();

  const [typeFilter, setTypeFilter] = useState("ALL");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [searchText, setSearchText] = useState("");

  const sanitizedFilters = useMemo(() => {
    return {
      type: typeFilter !== "ALL" ? typeFilter : undefined,
      from: fromDate || undefined,
      to: toDate || undefined,
      search: searchText.trim() || undefined,
    };
  }, [typeFilter, fromDate, toDate, searchText]);

  const {
    data: levelData,
    isLoading: levelLoading,
    error: levelError,
  } = useQuery({
    queryKey: ["inventory", "stock-level", stockLevelId],
    queryFn: () => getStockLevel(token, stockLevelId),
    enabled: Boolean(token && stockLevelId),
    refetchInterval: 10000,
    refetchOnWindowFocus: false,
  });

  const level = useMemo(() => resolveStockLevel(levelData), [levelData]);

  const {
    data: journalData,
    isLoading: journalLoading,
    isFetching: journalFetching,
    error: journalError,
  } = useQuery({
    queryKey: ["inventory", "stock-level", stockLevelId, "journal", sanitizedFilters],
    queryFn: () =>
      getStockJournal(token, stockLevelId, {
        ...sanitizedFilters,
      }),
    enabled: Boolean(token && stockLevelId),
    keepPreviousData: true,
    refetchInterval: 10000,
    refetchOnWindowFocus: false,
  });

  const journalEntries = useMemo(() => resolveJournalEntries(journalData), [journalData]);

  const available = buildAvailability(level);
  const locationLabel = useMemo(() => {
    if (!level) {
      return "—";
    }
    const name = level.location?.name || level.locationName;
    const code = level.location?.code || level.locationCode;
    if (name && code) {
      return `${name} · ${code}`;
    }
    return name || code || "—";
  }, [level]);

  const articleLabel = useMemo(() => {
    if (!level) {
      return "Unbekannter Artikel";
    }
    const name = level.item?.name || level.itemName || level.partName || level.description;
    const sku = level.item?.sku || level.sku || level.partNumber;
    if (name && sku) {
      return `${name} (${sku})`;
    }
    return name || sku || "Unbekannter Artikel";
  }, [level]);

  const assignedRoles = useMemo(
    () => (Array.isArray(roles) ? roles : roles ? [roles] : []),
    [roles],
  );
  const canAdjustStock =
    (typeof hasRole === "function" && (hasRole("ADMIN") || hasRole("LAGERIST"))) ||
    assignedRoles.includes("ADMIN") ||
    assignedRoles.includes("LAGERIST");

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack spacing={3}>
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="h4" component="h1">
              Lagerdetail
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              {articleLabel}
            </Typography>
          </Box>
          <Button variant="outlined" onClick={() => navigate("/inventory")}
        >
            Zurück
          </Button>
        </Stack>
        {levelLoading ? (
          <Paper sx={{ py: 6, display: "flex", justifyContent: "center" }}>
            <CircularProgress />
          </Paper>
        ) : levelError ? (
          <Paper sx={{ py: 6, px: 3 }}>
            <Typography color="error" variant="body1">
              {levelError.message || "Bestand konnte nicht geladen werden."}
            </Typography>
          </Paper>
        ) : !level ? (
          <Paper sx={{ py: 6, px: 3 }}>
            <Typography variant="body1">Bestand wurde nicht gefunden.</Typography>
          </Paper>
        ) : (
          <Paper sx={{ p: 3 }}>
            <Stack spacing={2}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={3}
                sx={{
                  "& > *": {
                    minWidth: { md: 160 },
                  },
                }}
              >
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Standort
                  </Typography>
                  <Typography variant="body1">{locationLabel}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Bestand
                  </Typography>
                  <Typography variant="h5">{formatNumber(level.onHand)}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Reserviert
                  </Typography>
                  <Typography variant="h5">{formatNumber(level.reserved)}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Verfügbar
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="h5">{formatNumber(available)}</Typography>
                    <Chip
                      label={available > 0 ? "verfügbar" : available < 0 ? "negativ" : "ausgeglichen"}
                      color={available > 0 ? "success" : available < 0 ? "error" : "default"}
                      size="small"
                      variant={available > 0 ? "filled" : "outlined"}
                    />
                  </Stack>
                </Box>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Letzte Aktualisierung: {formatDateTime(level.updatedAt)}
              </Typography>
              {canAdjustStock && (
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <Button variant="contained" color="primary">
                    Umbuchen
                  </Button>
                  <Button variant="outlined" color="secondary">
                    Korrigieren
                  </Button>
                </Stack>
              )}
            </Stack>
          </Paper>
        )}
        <Paper sx={{ p: 3, position: "relative" }}>
          <Stack spacing={2}>
            <Typography variant="h6">Bewegungsjournal</Typography>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <FormControl sx={{ minWidth: { md: 200 } }}>
                <InputLabel id="journal-type-label">Bewegung</InputLabel>
                <Select
                  labelId="journal-type-label"
                  label="Bewegung"
                  value={typeFilter}
                  onChange={(event) => setTypeFilter(event.target.value)}
                >
                  {transactionTypeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Von"
                type="date"
                value={fromDate}
                onChange={(event) => setFromDate(event.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Bis"
                type="date"
                value={toDate}
                onChange={(event) => setToDate(event.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Filter"
                placeholder="Referenz, Benutzer oder Notiz"
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
              />
            </Stack>
            {journalFetching && !journalLoading && (
              <LinearProgress sx={{ position: "absolute", top: 0, left: 0, right: 0 }} />
            )}
            {journalLoading ? (
              <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
                <CircularProgress />
              </Box>
            ) : journalError ? (
              <Box sx={{ py: 4 }}>
                <Typography color="error" variant="body1">
                  {journalError.message || "Bewegungsjournal konnte nicht geladen werden."}
                </Typography>
              </Box>
            ) : journalEntries.length === 0 ? (
              <Box sx={{ py: 4 }}>
                <Typography variant="body1">Keine Bewegungen im gewählten Zeitraum.</Typography>
              </Box>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Datum</TableCell>
                    <TableCell>Typ</TableCell>
                    <TableCell align="right">Menge</TableCell>
                    <TableCell>Ort</TableCell>
                    <TableCell>Referenz</TableCell>
                    <TableCell>Hinweis</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {journalEntries.map((entry) => {
                    const quantity = typeof entry.quantity === "number" ? entry.quantity : 0;
                    const transactionType = entry.type || entry.transactionType;
                    const locationName = entry.location?.name || entry.locationName || "—";
                    const locationCode = entry.location?.code || entry.locationCode;
                    const reference = entry.referenceLabel || entry.referenceType || "—";
                    const referenceId = entry.referenceId;
                    const note = entry.note || entry.comment || "";
                    const locationDisplay = locationCode
                      ? `${locationName} · ${locationCode}`
                      : locationName;
                    return (
                      <TableRow key={entry.id || `${entry.createdAt}-${entry.type}-${quantity}`}> 
                        <TableCell>{formatDateTime(entry.createdAt)}</TableCell>
                        <TableCell>{transactionType || "—"}</TableCell>
                        <TableCell align="right">{formatNumber(quantity)}</TableCell>
                        <TableCell>{locationDisplay}</TableCell>
                        <TableCell>
                          {referenceId ? `${reference} (${referenceId})` : reference}
                        </TableCell>
                        <TableCell>{note || "—"}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </Stack>
        </Paper>
      </Stack>
    </Container>
  );
}
